import React from 'react';
import { Columns3, Check, GripVertical } from 'lucide-react';
import { getColumnPreference, saveColumnPreference } from './services/api';
import "../../styles/components/column-visibility.scss"

const SAVE_DEBOUNCE_MS = 500;

function mergeOrder(columns, order) {
  if (!order || !order.length) return columns;
  const byId = new Map(columns.map((c) => [c.id, c]));
  const ordered = order.map((id) => byId.get(id)).filter(Boolean);
  const seen = new Set(order);
  columns.forEach((c) => {
    if (!seen.has(c.id)) ordered.push(c);
  });
  return ordered;
}
 

export { mergeOrder as applyColumnOrder };
 
export default class ColumnVisibilityDropdown extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false, hidden: [], order: [], loading: true, draggedId: null };
    this.wrapperRef = React.createRef();
    this.saveTimer = null;
  }

  componentDidMount() {
    this.loadPreference();
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tableKey !== this.props.tableKey) {
      this.loadPreference();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    if (this.saveTimer) clearTimeout(this.saveTimer);
  }

  handleClickOutside = (e) => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(e.target)) {
      this.setState({ open: false });
    }
  };

  loadPreference = async () => {
    this.setState({ loading: true });
    try {
      const res = await getColumnPreference(this.props.tableKey);
      const hidden = res?.data?.hidden_columns || [];
      const order = res?.data?.column_order || [];
      this.setState({ hidden, order });
      this.props.onChange(hidden, mergeOrder(this.props.columns, order).map((c) => c.id));
    } catch (e) {
      this.setState({ hidden: [], order: [] });
      this.props.onChange([], this.props.columns.map((c) => c.id));
    } finally {
      this.setState({ loading: false });
    }
  };

  toggleColumn = (id) => {
    const { hidden } = this.state;
    const next = hidden.includes(id) ? hidden.filter((c) => c !== id) : [...hidden, id];
    this.commit({ hidden: next });
  };
 
  toggleAll = () => {
    const { columns } = this.props;
    const toggleable = columns.filter((c) => !c.alwaysVisible);
    const allVisible = toggleable.every((c) => !this.state.hidden.includes(c.id));
 
    const next = allVisible ? toggleable.map((c) => c.id) : [];
    this.commit({ hidden: next });
  };
 
  // ── Drag-to-reorder ──
  handleDragStart = (id) => {
    this.setState({ draggedId: id });
  };
 
  handleDragOverItem = (e, overId) => {
    e.preventDefault();
    const { draggedId } = this.state;
    if (!draggedId || draggedId === overId) return;
 
    const current = mergeOrder(this.props.columns, this.state.order).map((c) => c.id);
    const from = current.indexOf(draggedId);
    const to = current.indexOf(overId);
    if (from === -1 || to === -1) return;
 
    const next = [...current];
    next.splice(from, 1);
    next.splice(to, 0, draggedId);
    this.setState({ order: next });
  };
 
  handleDragEnd = () => {
    this.setState({ draggedId: null });
    this.commit({ order: this.state.order });
  };
 
  commit = ({ hidden, order }) => {
    const nextHidden = hidden !== undefined ? hidden : this.state.hidden;
    const nextOrder = order !== undefined ? order : this.state.order;
 
    this.setState({ hidden: nextHidden, order: nextOrder });
    this.props.onChange(nextHidden, mergeOrder(this.props.columns, nextOrder).map((c) => c.id));
 
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      saveColumnPreference(this.props.tableKey, nextHidden, nextOrder).catch(() => {
      });
    }, SAVE_DEBOUNCE_MS);
  };

  render() {
    const { columns } = this.props;
    const { open, hidden, order, loading, draggedId } = this.state;
    const visibleCount = columns.filter((c) => !hidden.includes(c.id)).length;
    const toggleable = columns.filter((c) => !c.alwaysVisible);
    const allVisible = toggleable.every((c) => !hidden.includes(c.id));
    const someVisible = toggleable.some((c) => !hidden.includes(c.id));
    const displayColumns = mergeOrder(columns, order);
 
    return (
      <div className="column-visibility" ref={this.wrapperRef}>
        <button
          type="button"
          className="button1 button1-primary"
          onClick={() => this.setState({ open: !open })}
        >
          <Columns3 size={16} />
          Columns ({visibleCount}/{columns.length})
        </button>

        {open && (
          <div className="column-visibility__menu">
            {loading ? (
              <div className="column-visibility__loading">Loading...</div>
            ) : (
              <>
                <label className="column-visibility__item column-visibility__item--select-all">
                  <input
                    type="checkbox"
                    checked={allVisible}
                    ref={(el) => el && (el.indeterminate = !allVisible && someVisible)}
                    onChange={this.toggleAll}
                  />
                  <span className="column-visibility__checkbox">
                    {allVisible && <Check size={12} />}
                  </span>
                  Select all
                </label>
                <div className="column-visibility__divider" />
                {displayColumns.map((col) => {
                const isVisible = !hidden.includes(col.id);
                  const isDragging = draggedId === col.id;
                  return (
                    <label
                      key={col.id}
                      draggable
                      onDragStart={() => this.handleDragStart(col.id)}
                      onDragOver={(e) => this.handleDragOverItem(e, col.id)}
                      onDragEnd={this.handleDragEnd}
                      className={`column-visibility__item ${col.alwaysVisible ? 'is-locked' : ''} ${isDragging ? 'is-dragging' : ''}`}
                    >
                      <span className="column-visibility__handle">
                        <GripVertical size={13} />
                      </span>
                      <input
                        type="checkbox"
                        checked={isVisible}
                        disabled={col.alwaysVisible}
                        onChange={() => this.toggleColumn(col.id)}
                      />
                      <span className="column-visibility__checkbox">
                        {isVisible && <Check size={12} />}
                      </span>
                      {col.label}
                    </label>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}